import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import {
  get as _get,
  take
} from 'lodash';

import { FacilityActionType } from './facilities-action-types';
import { AuthActionType } from '../auth/auth-action-types';

import { Navigations } from '../../settings/nav-config';
import { getAtError } from '../../settings/error';

import {
  atGetFacilities,
  atPostFacility,
  atDeleteFacilities,
  atGetUserReportUsage,
  atGetDataDownload,
  atGetPromisExport,
  atPostSendInvite,
  atPostSendConsent,
  atRecoverUsers,
  atRemoveUsers,
  atPermanentDeleteUser,
} from '../../services/at-app-facilities-api';

function* getFacilities(action: any) {
  try {
    const data = yield call(
      atGetFacilities,
      action.payload
    );
    data.data.active = action.payload.active;
    yield put({ type: FacilityActionType.GetFacilitiesSuccess, payload: { type: action.payload.targetType, data: data } });
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.GetFacilitiesFailure, message: error });
    }
  }
}

function* getUserReportUsage(action: any) {
  try {
    const data = yield call(
      atGetUserReportUsage,
      action.payload
    );
    yield put({ type: FacilityActionType.GetUserReportUsageSuccess, payload: { type: action.payload.targetType, data: data } });
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.GetUserReportUsageFailure, message: error });
    }
  }
}

function* getDataDownload(action: any) {
  try {
    const data = yield call(
      atGetDataDownload,
      action.payload
    );
    yield put({ type: FacilityActionType.GetDataExportSuccess, payload: { type: action.payload.targetType, data: data, isSuperUser:action.payload.isSuperUser } });
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.GetDataExportFailure, message: error });
    }
  }
}
function* getPromisExport(action: any) {
  try {
    const data = yield call(atGetPromisExport, action.payload);
    yield put({ type: FacilityActionType.GetPromisExportSuccess, payload: { data } });
  } catch (err) {
    if (err.response && err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.GetPromisExportFailure, message: error });
    }
  }
}

function* postFacility(action: any) {
  try {
    const data = yield call(atPostFacility, action.payload);
    yield put({ type: FacilityActionType.PostFacilitySuccess, payload: { type: action.payload.targetType, data: data, sourceData: action.payload.body} });
    
    // Check if response contains email delivery status
    const emailSent = _get(data, ['data', 'emailSent'], true);
    const emailWarning = _get(data, ['data', 'emailWarning']);
    
    if (emailSent === false && emailWarning) {
      // User was created but email failed
      toast.success(`Save successful`);
      toast.warn(`Warning: ${emailWarning}`);
    } else {
      toast.success(`Save successful`);
    }

    if (
      [
        Navigations.facilities.hospitals,
        Navigations.facilities.clinics,
        Navigations.users.therapists,
        Navigations.users.patients,
        Navigations.users.staff,
      ].indexOf(action.payload.targetType) >=0
    ) {
      window.history.back();
    }
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.PostFacilityFailure, message: error });
    }
  }
}

function* deleteFacilities(action: any) {
  try {
    let data = yield call(atDeleteFacilities, action.payload);
    yield put({ type: FacilityActionType.DeleteFacilitiesSuccess, payload: { type: action.payload.targetType, data: action.payload.body } });
    toast.success(`The account has been deleted.`);
    
    data = yield call(
      atGetFacilities,
      action.payload
    );
    data.data.active = action.payload.active;
    yield put({ type: FacilityActionType.GetFacilitiesSuccess, payload: { type: action.payload.targetType, data: data } });
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.DeleteFacilitiesFailure, message: error });
    }
  }
}

function* recoverUsers(action: any) {
  try {
    let data = yield call(atRecoverUsers, action.payload);
    yield put({ type: FacilityActionType.RecoverUsersSuccess, payload: { type: action.payload.targetType, data: action.payload.body } });
    toast.success(`The account has been recovered.`);
    
    data = yield call(
      atGetFacilities,
      action.payload
    );
    data.data.active = action.payload.active;
    yield put({ type: FacilityActionType.GetFacilitiesSuccess, payload: { type: action.payload.targetType, data: data } });
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.RecoverUsersFailure, message: error });
    }
  }
}

function* removeUsers(action: any) {
  try {
    let data = yield call(atRemoveUsers, action.payload);
    yield put({ type: FacilityActionType.RemoveUsersSuccess, payload: { type: action.payload.targetType, data: action.payload.body } });
    toast.success(`The user account has been removed.`);
    
    data = yield call(
      atGetFacilities,
      action.payload
    );
    data.data.active = action.payload.active;
    yield put({ type: FacilityActionType.GetFacilitiesSuccess, payload: { type: action.payload.targetType, data: data } });
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.RemoveUsersFailure, message: error });
    }
  }
}

function* postSendInvite(action: any) {
  try {
    const data = yield call(atPostSendInvite, action.payload);
    yield put({ type: FacilityActionType.PostUserSendInviteSuccess, payload: { type: action.payload.targetType, data: data, sourceData: action.payload.body} });
    
    // Check if response contains email delivery status
    const emailSent = _get(data, ['data', 'emailSent'], true);
    const emailWarning = _get(data, ['data', 'emailWarning']);
    
    if (emailSent === false && emailWarning) {
      toast.warn(`Warning: ${emailWarning}`);
    } else {
      toast.success(`The email has been sent!`);
    }
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.PostUserSendInviteFailure, message: error });
    }
  }
}

function* permanentDeleteUser(action: any) {
  try {
    const data = yield call(atPermanentDeleteUser, action.payload);
    const username = action.payload.body[0].username || (action.payload.body[0].firstName + ' ' + action.payload.body[0].lastName);
    yield put({ type: FacilityActionType.PermanentDeleteUserSuccess, payload: { type: action.payload.targetType, data: action.payload.body } });
    toast.success(`User ${username} has been permanently deleted.`);

    const refreshData = yield call(
      atGetFacilities,
      { ...action.payload, active: false }
    );
    refreshData.data.active = false;
    yield put({ type: FacilityActionType.GetFacilitiesSuccess, payload: { type: action.payload.targetType, data: refreshData } });
  } catch (err) {
    if (err.response && err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.PermanentDeleteUserFailure, message: error });
    }
  }
}

function* postSendConsent(action: any) {
  try {
    const data = yield call(atPostSendConsent, action.payload);
    yield put({ type: FacilityActionType.PostUserSendConsentSuccess, payload: { type: action.payload.targetType, data: data, sourceData: action.payload.body} });
    toast.success(`The email has been sent!`);
  } catch (err) {
    if (err.response.status === 401) {
      yield put({ type: AuthActionType.Logout, payload: null });
    } else {
      const error = getAtError(err);
      toast.error(`${error}`);
      yield put({ type: FacilityActionType.PostUserSendConsentFailure, message: error });
    }
  }
}
const facilitiesSagas = [
  takeEvery(FacilityActionType.GetFacilities, getFacilities),
  takeEvery(FacilityActionType.PostFacility, postFacility),
  takeEvery(FacilityActionType.DeleteFacilities, deleteFacilities),
  takeEvery(FacilityActionType.GetUserReportUsage, getUserReportUsage),
  takeEvery(FacilityActionType.GetDataExport, getDataDownload),
  takeEvery(FacilityActionType.GetPromisExport, getPromisExport),
  takeEvery(FacilityActionType.PostUserSendInvite, postSendInvite),
  takeEvery(FacilityActionType.PostUserSendConsent, postSendConsent),
  takeEvery(FacilityActionType.RecoverUsers, recoverUsers),
  takeEvery(FacilityActionType.RemoveUsers, removeUsers),
  takeEvery(FacilityActionType.PermanentDeleteUser, permanentDeleteUser),
];

export {
    facilitiesSagas,
}
